'use client';

import { useState } from 'react';

import { faVideoSlash, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button, Dialog, DialogClose, DialogContent } from '@/shared-ui';
import {
  classifyMediaAsset,
  getExternalEmbedSandbox,
  getVideoEmbedUrl,
} from '@/shared-utilities';

import { ProjectResponseComponentProps } from './project-response-renderer.type';

const VIDEO_CLASSNAME =
  'my-1.25 aspect-video max-h-86 w-full max-w-full overflow-hidden rounded-lg';

interface ProjectResponseAssetItemProps {
  asset: NonNullable<ProjectResponseComponentProps['assets']>[number];
  compact?: boolean;
}

export function ProjectResponseAssetItem({
  asset,
  compact = false,
}: ProjectResponseAssetItemProps) {
  const t = useTranslations('Chat');
  const [hasVideoError, setHasVideoError] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const assetKind = classifyMediaAsset(asset.url);
  const isImage = assetKind === 'image';

  const renderVideoError = () => (
    <div
      data-test='asset-video-error'
      className='my-1.25 flex w-full max-w-full items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-700'
    >
      <FontAwesomeIcon
        icon={faVideoSlash}
        className='size-5 shrink-0 text-zinc-500'
        aria-hidden='true'
      />
      <div className='min-w-0 flex-1'>
        <div className='text-sm font-medium'>{t('videoLoadError')}</div>
        {asset.name ? (
          <div className='truncate text-xs text-zinc-500' title={asset.name}>
            {asset.name}
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderMedia = () => {
    if (hasImageError) {
      return null;
    }

    if (hasVideoError && !isImage) {
      return renderVideoError();
    }

    if (isImage) {
      if (compact) {
        return (
          <img
            src={asset.url}
            alt={asset.name || t('assetImageAlt')}
            className='size-full rounded-sm object-contain'
            onError={() => setHasImageError(true)}
            data-test='asset-image'
          />
        );
      }
      return (
        <>
          <button
            type='button'
            className='mb-2 w-full max-w-72'
            onClick={() => setIsImageModalOpen(true)}
            data-state='thumbnail'
            data-test='asset-image-button'
          >
            <img
              src={asset.url}
              alt={asset.name || t('assetImageAlt')}
              className='max-h-96 w-full rounded-lg object-cover'
              onError={() => setHasImageError(true)}
              data-test='asset-image'
            />
          </button>
          <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
            <DialogContent className='flex h-full w-9/10 max-w-480 items-center justify-center bg-white/70'>
              <DialogClose asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='absolute top-4 right-4 z-10 size-10 rounded-full bg-black/60 text-white hover:enabled:bg-black/80'
                >
                  <FontAwesomeIcon icon={faXmark} />
                </Button>
              </DialogClose>
              <img
                src={asset.url}
                alt={asset.name || t('assetImageAlt')}
                className='size-full object-contain'
              />
            </DialogContent>
          </Dialog>
        </>
      );
    }

    if (assetKind === 'external-video') {
      const embedUrl = getVideoEmbedUrl(asset.url);
      if (!embedUrl) {
        return renderVideoError();
      }

      const externalPlayer = (
        <iframe
          src={embedUrl}
          title={asset.name || t('assetVideoTitle')}
          className='size-full border-0'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          allowFullScreen
          referrerPolicy='strict-origin-when-cross-origin'
          loading='lazy'
          sandbox={getExternalEmbedSandbox(embedUrl)}
          data-test='asset-video-player-external'
        />
      );
      if (compact) {
        return (
          <div className='size-full overflow-hidden'>{externalPlayer}</div>
        );
      }
      return (
        <div className='my-1.25 aspect-video max-h-86 w-full max-w-full min-w-72 overflow-hidden rounded-lg'>
          {externalPlayer}
        </div>
      );
    }

    const player = (
      <video
        src={asset.url}
        controls
        muted
        playsInline
        preload='metadata'
        className={VIDEO_CLASSNAME}
        onError={() => setHasVideoError(true)}
        data-test='asset-video-player'
      />
    );
    if (compact) {
      return (
        <div className='size-full overflow-hidden [&_video]:max-h-full [&_video]:object-contain'>
          {player}
        </div>
      );
    }
    return player;
  };

  return (
    <div
      data-test='project-response-asset-item'
      data-compact={compact}
      className={
        compact
          ? 'flex size-full min-h-0 max-w-full items-center justify-center overflow-hidden'
          : 'not-prose asset-item-rounded flex w-full min-w-64 items-center justify-center sm:px-16'
      }
    >
      {renderMedia()}
    </div>
  );
}
